import React, { useState } from 'react';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Dot } from '../../../../../../../../CommonComponents/OPL/Dot/Dot';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { Tooltip } from '../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { ItemData } from '../../../../useAssetBucketingResults';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Wrapper } from './Item.styles';
import { Icon } from '../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import {
  AssetToMoveHoldings,
  MoveHoldingsDrawer,
} from '../../../../../AssetBucketingEdit/components/MoveHoldingsDrawer/MoveHoldingsDrawer';
import { HoldingDTO } from '../../../../api/useAssetBucketingResultsQuery';
import { HoldingsModal } from '../../../HoldingsModal/HoldingsModal';
import { RemoveAssetFn } from '../../../../../AssetBucketingEdit/AssetBucketingEdit.meta';

interface Props extends ItemData {
  as?: 'li';
  handleProps?: any;
  isDragging?: boolean;
  isEditable?: boolean;
  bucketIndex?: number;
  onRemove?: RemoveAssetFn;
}

export const Item = ({
  id,
  name,
  color,
  isOther,
  isUnassigned,
  handleProps,
  isDragging,
  isEditable,
  as,
  transactions,
  bucketIndex,
  holdings,
  isHoldingsBucketInheritedFromAsset,
  onRemove,
  displayValue,
  excludedFromNetworth,
}: Props) => {
  const [assetToMoveHoldings, setAssetToMoveHoldings] = useState<AssetToMoveHoldings | null>(null);

  const [holdingsBreakdown, setHoldingsBreakdown] = useState<HoldingDTO[] | null>(null);

  return (
    <>
      <Tooltip
        key={name}
        content="See full breakdown to view details"
        disabled={!isOther}
        placement="top"
      >
        <Wrapper
          as={as}
          isDragging={isDragging}
          isUnassigned={isUnassigned}
          isEditable={isEditable}
        >
          {isEditable && (
            <Box flexShrink={0}>
              <IconButton
                variant={isDragging ? 'link' : 'subtleInactive'}
                css="cursor: grab"
                icon="fas fa-grip-vertical"
                aria-label={`${name} drag handle`}
                {...handleProps}
              />
            </Box>
          )}

          <Box flex={1} {...(displayValue === null ? { alignSelf: 'center' } : {})}>
            <Box display="flex" css="gap: 6px">
              {color && <Dot bg={color} mt={2} />}

              <Box flex={1}>
                <Box display="flex" css="gap: 6px">
                  <Text
                    fontSize="small"
                    fontWeight={500}
                    color="neutral.600"
                    isOverflowWrap
                    css="flex: 1"
                  >
                    {`${name}${excludedFromNetworth ? '*' : ''}`}
                  </Text>

                  {isHoldingsBucketInheritedFromAsset && (
                    <Icon className="far fa-briefcase" color="neutral.500" />
                  )}

                  {transactions.length > 0 && (
                    <Icon color="neutral.500" className="far fa-exchange" />
                  )}
                </Box>

                {displayValue !== null && (
                  <Text variant="tabular" fontWeight={500}>
                    {!isUnassigned && formatCurrency(displayValue)}

                    {transactions.length > 0 && (
                      <Tooltip
                        content={
                          <>
                            {transactions.map(transaction => (
                              <Text color="white" fontSize="small">
                                {transaction.label}:{' '}
                                <strong>{formatCurrency(transaction.value)}</strong>
                              </Text>
                            ))}
                          </>
                        }
                      >
                        <Icon ml="4" color="neutral.500" className="far fa-info-circle" />
                      </Tooltip>
                    )}

                    {!isEditable && holdings.length > 0 && (
                      <Text color="neutral.600" fontWeight={500} fontSize="small">
                        {holdings.length} Holding(s)
                        <IconButton
                          icon="far fa-info-circle"
                          variant="subtleInactive"
                          size="small"
                          onClick={() => setHoldingsBreakdown(holdings)}
                          aria-label="Open holdings breakdown modal"
                        />
                      </Text>
                    )}
                  </Text>
                )}
              </Box>
            </Box>

            {isEditable && holdings.length > 0 && (
              <Button
                variant="secondary"
                size="small"
                css="align-self: flex-start"
                mt="4"
                onClick={() =>
                  setAssetToMoveHoldings({ id, name, holdings, bucketIndex: bucketIndex ?? 0 })
                }
              >
                Move Holdings
              </Button>
            )}
          </Box>

          {!isUnassigned && onRemove && (
            <IconButton
              aria-label="Make unassigned"
              variant="subtleInactive"
              icon="far fa-minus-circle"
              onClick={() => onRemove({ assetId: id, bucketIndex: bucketIndex ?? 0 })}
              css="align-self: center"
            >
              Remove
            </IconButton>
          )}
        </Wrapper>
      </Tooltip>

      {assetToMoveHoldings && (
        <MoveHoldingsDrawer
          asset={assetToMoveHoldings}
          onClose={() => setAssetToMoveHoldings(null)}
        />
      )}

      {holdingsBreakdown && (
        <HoldingsModal holdings={holdingsBreakdown} onClose={() => setHoldingsBreakdown(null)} />
      )}
    </>
  );
};
